import { createSlice } from '@reduxjs/toolkit';
import axios from '../../utils/axios';

const API = process.env.REACT_APP_HOST_API;

const initialState = {
  isLoading: false,
  error: null,
  list: { items: [], total: 0, page: 1, limit: 25 },
  filters: { search: '', role: 'all', tier: 'all', status: 'all', sort: 'createdAt', order: 'desc' },
  current: null,
  isLoadingCurrent: false,
};

const slice = createSlice({
  name: 'adminUsers',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.isLoadingCurrent = false;
      state.error = action.payload;
    },
    setList(state, action) {
      state.isLoading = false;
      state.list = action.payload;
    },
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },
    startLoadingCurrent(state) {
      state.isLoadingCurrent = true;
      state.error = null;
    },
    setCurrent(state, action) {
      state.isLoadingCurrent = false;
      state.current = action.payload;
    },
  },
});

export default slice.reducer;
export const { setFilters } = slice.actions;

const toQuery = (filters, page, limit) => {
  const params = { page, limit, sort: filters.sort, order: filters.order };
  if (filters.search) params.search = filters.search;
  if (filters.role && filters.role !== 'all') params.role = filters.role;
  if (filters.tier && filters.tier !== 'all') params.tier = filters.tier;
  if (filters.status && filters.status !== 'all') params.status = filters.status;
  return params;
};

export function fetchUsers({ page = 1, limit = 25 } = {}) {
  return async (dispatch, getState) => {
    dispatch(slice.actions.startLoading());
    try {
      const { filters } = getState().adminUsers;
      const response = await axios.get(`${API}/admin/users`, { params: toQuery(filters, page, limit) });
      dispatch(slice.actions.setList(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.error?.message || 'Admin/Users/Fetch/InternalError'));
    }
  };
}

export function fetchUser(uid) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoadingCurrent());
    try {
      const response = await axios.get(`${API}/admin/users/${uid}`);
      dispatch(slice.actions.setCurrent(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.error?.message || 'Admin/Users/Fetch/InternalError'));
    }
  };
}

export function updateUserRole(uid, role) {
  return async (dispatch) => {
    await axios.patch(`${API}/admin/users/${uid}/role`, { role });
    dispatch(fetchUser(uid));
  };
}

export function updateUserPlan(uid, plan) {
  return async (dispatch) => {
    await axios.patch(`${API}/admin/users/${uid}/plan`, { plan });
    dispatch(fetchUser(uid));
  };
}

export function updateUserStatus(uid, disabled) {
  return async (dispatch) => {
    await axios.patch(`${API}/admin/users/${uid}/status`, { disabled });
    dispatch(fetchUser(uid));
  };
}

export function updateUserResources(uid, resources) {
  return async (dispatch) => {
    await axios.patch(`${API}/admin/users/${uid}/resources`, resources);
    dispatch(fetchUser(uid));
  };
}

export function sendPasswordReset(uid) {
  return async () => {
    await axios.post(`${API}/admin/users/${uid}/password-reset`);
  };
}

export function deleteUser(uid) {
  return async () => {
    await axios.delete(`${API}/admin/users/${uid}`);
  };
}
